import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import useAuthStore from '../store/auth-store'
import ForgotPasswordModal from './ForgotPasswordModal'
import { Eye, EyeOff, X, Loader, ArrowLeft, ShieldCheck } from 'lucide-react'
import mailIcon from '/images/mail_24dp_E3E3E3_FILL0_wght400_GRAD0_opsz24.png'
import phoneIcon from '/images/phone_enabled_24dp_E3E3E3_FILL1_wght400_GRAD0_opsz24.png'

// ─── 6-box OTP input ──────────────────────────────────────────────────────────
// `onComplete(fullValue)` fires the moment the user finishes the 6th digit. It
// is passed the FRESH value, not state — callers should use this for submitting
// instead of reading their own state (which may be one tick behind).
function OtpInput({ value, onChange, onComplete, disabled }) {
  const refs = useRef([null, null, null, null, null, null]).current

  const setOtp = (next) => {
    const clean = (next || '').replace(/\D/g, '').slice(0, 6)
    onChange(clean)
    return clean
  }

  const handleKey = (e, idx) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      if (value[idx]) {
        // Clear this box
        setOtp(value.substring(0, idx) + value.substring(idx + 1))
      } else if (idx > 0) {
        // Empty box → step back and clear the previous one
        setOtp(value.substring(0, idx - 1) + value.substring(idx))
        refs[idx - 1]?.focus()
      }
    }
  }

  const handleChange = (e, idx) => {
    const ch = e.target.value.replace(/\D/g, '').slice(-1)
    if (!ch) return
    // Position-based replace; never squash empty middle slots
    const padded = value.padEnd(idx, ' ').substring(0, idx)
    const tail   = value.substring(idx + 1)
    const next   = setOtp(padded + ch + tail)
    if (idx < 5) refs[idx + 1]?.focus()
    if (next.length === 6 && onComplete) onComplete(next)
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    const next = setOtp(pasted)
    refs[Math.min(next.length, 5)]?.focus()
    if (next.length === 6 && onComplete) onComplete(next)
  }

  const boxCls = "w-11 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#358ECD] disabled:bg-gray-100 transition"

  return (
    <div className="flex items-center justify-center gap-2">
      {[0,1,2].map(i => (
        <input key={i} ref={el => refs[i] = el} type="text" inputMode="numeric" maxLength={1}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          disabled={disabled} value={value[i] || ''}
          onChange={e => handleChange(e, i)} onKeyDown={e => handleKey(e, i)} onPaste={handlePaste}
          className={boxCls} />
      ))}
      <span className="text-2xl font-light text-gray-400 mx-1">-</span>
      {[3,4,5].map(i => (
        <input key={i} ref={el => refs[i] = el} type="text" inputMode="numeric" maxLength={1}
          autoComplete="off"
          disabled={disabled} value={value[i] || ''}
          onChange={e => handleChange(e, i)} onKeyDown={e => handleKey(e, i)} onPaste={handlePaste}
          className={boxCls} />
      ))}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
export function SignInModal({ isOpen, onClose, onSwitchToCreateAccount }) {
  const navigate = useNavigate()
  const {
    login, verifyTwoFactor, resendEmail2faCode,
    isLoading, error: authError, pendingTwoFactor,
  } = useAuthStore()

  // Step-1 state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [authMethod, setAuthMethod] = useState('totp')   // 'totp' (Phone) | 'email'
  const [showForgot, setShowForgot] = useState(false)
  const [error, setError] = useState('')

  // Step-2 state
  const [otpValue,        setOtpValue]        = useState('')
  const [useBackupCode,   setUseBackupCode]   = useState(false)
  const [backupCodeValue, setBackupCodeValue] = useState('')
  const [resendCountdown, setResendCountdown] = useState(0)
  const [resendBusy, setResendBusy] = useState(false)
  const [resendMsg, setResendMsg] = useState(null) // { type: 'success'|'error', text }
  const verifySubmittingRef = useRef(false)        // Dedupe concurrent verify submits
  const chosenMethodRef     = useRef('totp')       // Sticky copy of the method the user actually picked

  // Derived step from pendingTwoFactor — 1 = credentials, 2 = OTP
  const step = pendingTwoFactor ? 2 : 1

  // ── Reset everything when the modal closes ────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      setEmail(''); setPassword(''); setShowPassword(false); setRememberMe(false)
      setAuthMethod('totp'); setError(''); setShowForgot(false)
      setOtpValue(''); setResendCountdown(0); setResendMsg(null)
      // Clear any leftover challenge in the store
      useAuthStore.setState({ pendingTwoFactor: null, error: null })
    }
  }, [isOpen])

  // ── When entering step-2 with email method, start the resend countdown ────
  useEffect(() => {
    if (pendingTwoFactor?.method === 'email' && pendingTwoFactor.resend_after) {
      setResendCountdown(pendingTwoFactor.resend_after)
    }
  }, [pendingTwoFactor?.method, pendingTwoFactor?.resend_after])

  // ── Tick the countdown ────────────────────────────────────────────────────
  useEffect(() => {
    if (resendCountdown <= 0) return
    const id = setInterval(() => setResendCountdown(c => Math.max(0, c - 1)), 1000)
    return () => clearInterval(id)
  }, [resendCountdown])

  if (!isOpen) return null

  // ── Step 1: submit credentials + chosen method ────────────────────────────
  const handleCredentials = async (e) => {
    e.preventDefault()
    setError(''); setResendMsg(null); setOtpValue('')
    if (!email || !password) {
      setError('Email and password are required')
      return
    }
    // Remember the user's actual choice in a ref so the verify call is never
    // ambiguous, even if Zustand state gets clobbered or read stale.
    chosenMethodRef.current = authMethod
    const result = await login(email, password, authMethod, rememberMe)
    if (!result.success) {
      setError(result.error || 'Login failed')
      return
    }
    if (!result.require_2fa) {
      // No 2FA configured — straight to dashboard
      setEmail(''); setPassword(''); setError('')
      navigate('/dashboard')
      onClose()
    }
    // require_2fa=true → pendingTwoFactor is now set, modal renders step 2
  }

  // ── Step 2: verify the code ──────────────────────────────────────────────
  // codeOverride lets OtpInput's onComplete pass the FRESH 6-char value without
  // relying on React state having flushed — this fixes "typing fails, paste
  // works" where state batching left otpValue one char behind on submit.
  const handleVerify = async (e, codeOverride) => {
    e?.preventDefault?.()
    if (verifySubmittingRef.current) return
    let code
    if (useBackupCode) {
      code = backupCodeValue.trim().toUpperCase()
      if (code.length !== 8 || isLoading) return
    } else {
      code = (codeOverride ?? otpValue).replace(/\D/g, '').slice(0, 6)
      if (code.length < 6 || isLoading) return
    }
    verifySubmittingRef.current = true
    try {
      const result = await verifyTwoFactor(
        pendingTwoFactor.temp_token,
        code,
        chosenMethodRef.current,
      )
      if (result.success) {
        navigate('/dashboard')
        onClose()
      } else {
        setOtpValue(''); setBackupCodeValue('')
      }
    } finally {
      verifySubmittingRef.current = false
    }
  }

  // ── Resend email code ─────────────────────────────────────────────────────
  const handleResend = async () => {
    if (resendBusy || resendCountdown > 0) return
    setResendBusy(true); setResendMsg(null)
    const r = await resendEmail2faCode(pendingTwoFactor.temp_token)
    setResendBusy(false)
    if (r.success) {
      setResendMsg({ type: 'success', text: r.message || 'A new code has been sent.' })
      setResendCountdown(r.resend_after || 60)
    } else {
      setResendMsg({ type: 'error', text: r.error })
      if (r.retry_after) setResendCountdown(r.retry_after)
    }
  }

  // ── Step 2 — switch back to step 1 (re-enter credentials) ─────────────────
  const handleBackToCredentials = () => {
    useAuthStore.setState({ pendingTwoFactor: null, error: null })
    setOtpValue(''); setBackupCodeValue(''); setUseBackupCode(false)
    setResendMsg(null); setResendCountdown(0)
  }

  // ════════════════════════════════════════════════════════════════════════
  // STEP 2 — OTP verification (TOTP or email)
  // ════════════════════════════════════════════════════════════════════════
  if (step === 2) {
    const isEmailMethod = pendingTwoFactor.method === 'email'
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-md relative">
          <button onClick={onClose}
            className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition z-10">
            <X className="w-6 h-6" />
          </button>

          <div className="p-6 space-y-5">
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-3">
                {isEmailMethod
                  ? <img src={mailIcon} alt="" className="w-7 h-7" />
                  : <ShieldCheck className="w-7 h-7 text-[#358ECD]" />}
              </div>
              <h2 className="text-xl font-bold text-gray-900">Two-Factor Authentication</h2>
              <p className="text-sm text-gray-500 mt-2 text-center">
                {isEmailMethod ? (
                  <>We sent a 6-digit code to <strong>{pendingTwoFactor.sent_to}</strong>.<br />Enter it below to continue.</>
                ) : (
                  <>Open <strong>Google Authenticator</strong> and enter the 6-digit code.</>
                )}
              </p>
            </div>

            {(error || authError) && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm text-center">
                {error || authError}
              </div>
            )}

            {resendMsg && (
              <div className={`px-4 py-2 rounded text-sm text-center border ${
                resendMsg.type === 'success'
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                {resendMsg.text}
              </div>
            )}

            <form onSubmit={handleVerify} className="space-y-5">
              {useBackupCode ? (
                <div>
                  <input
                    type="text"
                    value={backupCodeValue}
                    onChange={e => setBackupCodeValue(e.target.value.toUpperCase())}
                    placeholder="Enter 8-character backup code"
                    maxLength={8}
                    disabled={isLoading}
                    autoFocus
                    className="w-full text-center text-base font-mono tracking-widest border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:border-[#358ECD] disabled:bg-gray-100 uppercase"
                  />
                  <p className="text-xs text-gray-400 text-center mt-1.5">
                    Backup codes are 8 characters, e.g. <span className="font-mono">A3F2B9E1</span>
                  </p>
                </div>
              ) : (
                <OtpInput value={otpValue} onChange={setOtpValue}
                  onComplete={(fresh) => handleVerify(null, fresh)}
                  disabled={isLoading} />
              )}

              <button type="submit"
                disabled={isLoading || (useBackupCode ? backupCodeValue.trim().length !== 8 : otpValue.length < 6)}
                className="w-full font-bold py-3 px-4 rounded-lg transition
                           bg-[#358ECD] text-white hover:bg-[#2a6fa3] disabled:opacity-50 disabled:cursor-not-allowed">
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader className="w-4 h-4 animate-spin" /> Verifying...
                  </span>
                ) : 'CONFIRM'}
              </button>
            </form>

            {!isEmailMethod && (
              <div className="text-center">
                <button type="button"
                  onClick={() => { setUseBackupCode(v => !v); setOtpValue(''); setBackupCodeValue('') }}
                  className="text-xs text-gray-400 hover:text-[#358ECD] transition underline">
                  {useBackupCode ? 'Use Google Authenticator instead' : "Can't access your app? Use a backup code"}
                </button>
              </div>
            )}

            {isEmailMethod && (
              <div className="text-center text-sm">
                {resendCountdown > 0 ? (
                  <span className="text-gray-500">Resend available in {resendCountdown}s</span>
                ) : (
                  <button onClick={handleResend} disabled={resendBusy}
                    className="text-[#358ECD] hover:underline disabled:opacity-50">
                    {resendBusy ? 'Sending…' : 'Resend code'}
                  </button>
                )}
              </div>
            )}

            <button type="button" onClick={handleBackToCredentials}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mx-auto transition">
              <ArrowLeft className="w-4 h-4" /> Back to Sign In
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ════════════════════════════════════════════════════════════════════════
  // STEP 1 — Email + Password + 2FA Method
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div className="bg-overlay fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg relative">
        <button onClick={onClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition z-10">
          <X className="w-6 h-6" />
        </button>

        <form onSubmit={handleCredentials} className="space-y-3 signinmain">
          <div className="py-6 px-8 b-bt">
            <h2 className="text-2xl font-bold text-[#4D4D4D]">Sign In</h2>
          </div>

          {(error || authError) && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error || authError}
            </div>
          )}

          {/* Email */}
          <div className="py-6 px-8 signinform">
            <div className="flex items-center gap-4">
              <label className="text-lg font-bold text-[#4D4D4D] w-20 flex-shrink-0">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email"
                className="flex-1 px-3 py-1 border border-[#D6D6D6] rounded-lg focus:outline-none focus:border-[#358ECD] focus:ring-1 focus:ring-[#358ECD] text-[#4D4D4D] placeholder-[#D6D6D6]"
                required />
            </div>

            {/* Password */}
            <div className="flex items-center gap-4">
              <label className="text-lg font-bold text-[#4D4D4D] w-20 flex-shrink-0">Password</label>
              <div className="flex-1 relative">
                <input type={showPassword ? 'text' : 'password'} value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="Password"
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-[#358ECD] focus:ring-1 focus:ring-[#358ECD] text-[#4D4D4D] placeholder-[#D6D6D6]"
                  required />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember / Forgot */}
            <div className="flex justify-between items-center pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
                  className="w-4 h-4 border border-gray-300 rounded accent-[#358ECD] focus:ring-[#358ECD]" />
                <span className="common-size16 text-sm text-[#888888]">Remember Me</span>
              </label>
              <button type="button" className="common-size16 text-sm text-[#888888] underline bg-transparent border-none p-0 m-0 cursor-pointer"
                onClick={() => setShowForgot(true)}>Forgot Password?</button>
            </div>

            {/* 2FA Method */}
            <div className="pt-5">
              <label className="block text-lg font-bold text-[#4D4D4D] mb-3">2-Factor Authentication Method</label>
              <div className="flex gap-3">
                <button type="button" onClick={() => setAuthMethod('email')}
                  className={`flex-1 py-3 px-4 rounded-lg font-bold transition flex items-center justify-start gap-4 border-2 border-[#358ECD] text-[#358ECD] bg-white`}>
                  <img src={mailIcon} alt="" className="" />
                  <span className="text-base">Email</span>
                </button>
                <button type="button" onClick={() => setAuthMethod('totp')}
                  className={`flex-1 py-3 px-4 rounded-lg font-bold transition flex items-center justify-start gap-4 border-2 border-[#358ECD] bg-[#358ECD] text-white`}>
                  <img src={phoneIcon} alt="" className="" />
                  <span className="text-base">Phone</span>
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Only used if you have 2FA enabled. Otherwise sign-in proceeds directly.
              </p>
            </div>

            {/* Continue */}
            <button type="submit" disabled={isLoading}
              className={`common-size20 w-full font-bold py-2 px-2 md:py-5 md:px-4 rounded-2xl transition mt-4 ${
                isLoading ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-[#358ECD] text-white hover:bg-[#2a6fa3]'
              }`}>
              {isLoading ? 'SIGNING IN...' : 'CONTINUE'}
            </button>

            <div className="text-center pt-5">
              <p className="common-size16 text-sm text-[#4D4D4D] font-semibold">
                New to WQData LIVE?{' '}
                <button type="button" onClick={onSwitchToCreateAccount}
                  className="text-[#358ECD] hover:text-[#358ECD] underline transition">Create an Account</button>
              </p>
            </div>
          </div>
        </form>

        <ForgotPasswordModal open={showForgot} onClose={() => setShowForgot(false)} />
      </div>
    </div>
  )
}
